import React, { Component } from 'react';
import PropTypes from 'prop-types';
import LocaleStore from 'i18n/src/LocaleStore';
import AuthenticationStore from 'security/src/AuthenticationStore';
import BioCatchActions from 'security/src/BioCatchActions';
import { Provider } from 'mobx-react';
import Fetch from 'fetch';
import * as AnomalyDetection from 'common-ui/src/anomaly-detection';
import { Loadable } from 'loading';
import { Analytics } from 'common-ui/src/components/module';
import AppLoading from './loading/AppLoading';
import AppConfigStore from './app-config/AppConfigStore';
import ModernBrandingStore from './branding/ModernBrandingStore';
import './deps/usp_lib';
import './deps/auth';
import AppHelmet from './AppHelmet';
import NavigationStore from './navigation/stores/NavigationStore';
import AppLoaderErrorBoundary from './AppLoaderErrorBoundary';
import UrlSanitizer from './UrlSanitizer';
import CONSTANTS from './Constants';

class AppLoaderComponent extends Component {
    static defaultProps = {};

    static propTypes = {
        appType: PropTypes.string,
    };

    static appDisabledHandler() {
        const path = window.location.pathname;
        let contextPath = process.env.REACT_APP_CONTEXT;

        if (contextPath && contextPath.endsWith('/')) {
            contextPath = contextPath.substring(0, contextPath.length - 1);
        }

        if (path.indexOf(contextPath) === 0) {
            let uspContextPath = '/usp-core';

            if (path.indexOf('/live') !== -1) {
                uspContextPath = '/tob/live/usp-core';
            } else if (path.indexOf('/beta') !== -1) {
                uspContextPath = '/tob/beta/usp-core';
            }
            const routerPath = path.substring(
                path.indexOf(contextPath) + contextPath.length,
            );

            let finalPath = `${uspContextPath}${routerPath}`;

            if (window.location.href.indexOf('localhost') !== -1) {
                finalPath = `https://www.uspdev1.com${finalPath}`;
            }
            window.location.href = finalPath;
        }
    }

    static loadAnomalyDetectionWebSDK(appConfig) {
        const { callSignConfig, mobileFlow } = appConfig;
        const { orgId } = callSignConfig ?? {};

        const elementIdentifier = `script[src="${AnomalyDetection.WEB_SDK_URL}"]`;
        const element = window.top.document.querySelector(elementIdentifier);

        if (element != null) {
            console.log('Anomaly detection SDK has loaded already.');

            return;
        }

        if (callSignConfig) {
            const enabled =
                callSignConfig.enabled ||
                (mobileFlow && callSignConfig.sessionId);
            AnomalyDetection.setIsEnabled(enabled);
        }

        if (!AnomalyDetection.isEnabled()) {
            console.log('Anomaly detection is disabled.');
            AnomalyDetection.flushStorage();

            return;
        }

        if (!orgId) {
            console.log('Anomaly detection organization id is missing.');
            AnomalyDetection.flushStorage();

            return;
        }

        try {
            AnomalyDetection.loadWebSDK(callSignConfig);
        } catch (error) {
            console.error(
                'Error while loading the anomaly detection Web SDK.',
                error,
            );
        }
    }

    constructor() {
        super();
        Fetch.setErrorCodeHandler(
            CONSTANTS.ERROR_CODES.APP_DISABLED,
            AppLoaderComponent.appDisabledHandler,
        );

        this.loadablePromise = LocaleStore.loadLocale()
            .then((res) => {
                const { locale } = res ?? {};

                const sessionLng = locale === 'en' ? 'en-US' : locale;
                sessionStorage.setItem('locale', sessionLng);

                if (locale) {
                    const { appType } = this.props;

                    if (appType !== 'cloudflare') {
                        import('moment').then((moment) => {
                            const momentLocale = locale
                                .toLowerCase()
                                .replace('_', '-');
                            moment.locale(momentLocale);
                        });
                    }
                }

                return Promise.all([
                    AppConfigStore.loadAppConfig(),
                    AuthenticationStore.loadCurrentUser(),
                ]);
            })
            .then(() => {
                const { appConfig } = AppConfigStore; // loading the sdk as soon as the config is ready
                AppLoaderComponent.loadAnomalyDetectionWebSDK(appConfig);
                const promises = [];
                const modernCSSEnabled =
                    appConfig?.modernCSSStylingEnabled ?? false;

                if (appConfig.cdiFingerprintEnabled) {
                    promises.push(import('./deps/fingerprint2'));
                }

                if (modernCSSEnabled) {
                    promises.push(
                        ModernBrandingStore.loadModernBranding(appConfig.diId),
                    );
                }

                if (promises.length) {
                    return Promise.all(promises);
                }

                return Promise.resolve();
            })
            .then(() => {
                const { appType } = this.props;

                if (appType === 'cloudflare') {
                    return import('./cloudflare/CloudFlareApp');
                }

                return import('./App');
            });
    }

    state = {};

    render() {
        return (
            <Provider
                authenticationStore={AuthenticationStore}
                appConfigStore={AppConfigStore}
                modernBrandingStore={ModernBrandingStore}
                localeStore={LocaleStore}
                navigationStore={NavigationStore}
                bioCatchActions={BioCatchActions}
            >
                <Analytics key="analytics" />
                <React.Fragment>
                    <AppHelmet
                        appConfigStore={AppConfigStore}
                        localeStore={LocaleStore}
                        authenticationStore={AuthenticationStore}
                        modernBrandingStore={ModernBrandingStore}
                        pathname={window.location.pathname}
                    />
                    <AppLoaderErrorBoundary>
                        <UrlSanitizer>{this.getAppComponent()}</UrlSanitizer>
                    </AppLoaderErrorBoundary>
                </React.Fragment>
            </Provider>
        );
    }

    getAppComponent() {
        return (
            <Loadable
                loader={() => this.loadablePromise}
                loadingComponent={AppLoading}
            />
        );
    }
}

export default AppLoaderComponent;
